import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { getJobCategory } from './JobBlock';
import type { CalendarSlot } from '../../stores/calendarStore';

const CATEGORY_DOT_COLORS: Record<string, string> = {
  daily: 'bg-accent-purple',
  weekly: 'bg-info',
  monthly: 'bg-warning',
  recurring: 'bg-accent-purple',
};

function formatCountdown(scheduledAt: string, now: Date): string {
  const target = parseISO(scheduledAt);
  const diffMin = differenceInMinutes(target, now);

  if (diffMin < 0) return 'now';
  if (diffMin < 1) return 'in <1m';

  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;

  if (hours > 0) {
    return mins > 0 ? `in ${hours}h ${mins}m` : `in ${hours}h`;
  }
  return `in ${mins}m`;
}

interface NextUpQueueProps {
  slots: CalendarSlot[];
}

export function NextUpQueue({ slots }: NextUpQueueProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const sorted = [...slots].sort(
    (a, b) => a.scheduled_at.localeCompare(b.scheduled_at)
  );

  return (
    <div className="w-full lg:w-72 bg-bg-secondary rounded-lg p-4 border border-border-subtle">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-4 w-4 text-text-muted" />
        <h3 className="text-sm font-medium text-text-primary">Next Up</h3>
      </div>

      {sorted.length > 0 ? (
        <div className="space-y-3">
          {sorted.map((slot) => {
            const category = getJobCategory(slot.job_id);
            const dotColor = CATEGORY_DOT_COLORS[category];

            return (
              <div key={`${slot.job_id}-${slot.scheduled_at}`} className="flex items-start gap-2.5">
                <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {slot.job_name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-muted">
                      {format(parseISO(slot.scheduled_at), 'h:mm a')}
                    </span>
                    <span className="text-xs text-text-secondary">
                      {formatCountdown(slot.scheduled_at, now)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-text-muted text-center py-4">No upcoming jobs</p>
      )}
    </div>
  );
}

export default NextUpQueue;
